import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, User, ChevronRight, Tag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function stripHtml(html) {
  if (!html) return '';
  let text = html.replace(/<[^>]*>/g, '');
  // Remove leftover tag content from bad stripping (e.g. img src="..." alt="..." style="...")
  text = text.replace(/img\s+src=["'][^"']*["'][^.]*/gi, '');
  text = text.replace(/\s*(alt|style|class|src|href)=["'][^"']*["']\s*/gi, '');
  return text.replace(/\s+/g, ' ').trim();
}

function BlogList() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState({});
  const [activeCategory, setActiveCategory] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [postsRes, catsRes] = await Promise.all([
          fetch(`${API}/blog${activeCategory ? `?category=${activeCategory}` : ''}`),
          fetch(`${API}/blog/categories`)
        ]);
        if (postsRes.ok) setPosts(await postsRes.json());
        if (catsRes.ok) setCategories(await catsRes.json());
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [activeCategory]);

  return (
    <div className="min-h-screen bg-stone-50">
      <nav className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors" data-testid="blog-back-btn">
            <ArrowLeft size={20} />
            <span className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Gravsteinoppfriskeren
            </span>
          </button>
        </div>
      </nav>

      {/* Hero */}
      <div className="relative text-white py-20 md:py-28">
        <div className="absolute inset-0 z-0" style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_6f167cb2-c653-4d56-91af-258001cc5e51/artifacts/a8kh0vi3_Bilde%20fra%20Busknes%20gravplass.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}>
          <div className="absolute inset-0 bg-stone-900/60"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4">
          <h1 className="text-4xl sm:text-5xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="blog-page-title">
            Blogg
          </h1>
          <p className="text-base text-stone-300">Tips, nyheter og kunnskap om gravsteinspleie og DC-Protect</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Category filters */}
        {Object.keys(categories).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-categories">
            <button
              onClick={() => setActiveCategory('')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                !activeCategory ? 'bg-emerald-800 text-white' : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
              }`}
            >
              Alle
            </button>
            {Object.entries(categories).map(([cat, count]) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === cat ? 'bg-emerald-800 text-white' : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
                }`}
              >
                {cat} ({count})
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-800 rounded-full animate-spin mx-auto mb-4"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20" data-testid="blog-empty">
            <p className="text-stone-500 text-lg">Ingen innlegg ennå. Kom tilbake snart!</p>
          </div>
        ) : (
          <div className="space-y-8" data-testid="blog-posts-list">
            {/* Featured first post */}
            {posts.length > 0 && (
              <article
                onClick={() => navigate(`/blogg/${posts[0].slug}`)}
                className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer group"
                data-testid={`blog-post-${posts[0].slug}`}
              >
                {posts[0].cover_image && (
                  <div className="w-full h-64 md:h-80 overflow-hidden">
                    <img src={posts[0].cover_image} alt={posts[0].title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="p-8">
                  <div className="flex items-center gap-4 text-sm text-stone-400 mb-4">
                    {posts[0].category && (
                      <span className="flex items-center gap-1 bg-emerald-50 text-emerald-800 px-3 py-1 rounded-full text-xs font-semibold">
                        <Tag size={12} /> {posts[0].category}
                      </span>
                    )}
                    <span className="flex items-center gap-1"><Calendar size={14} /> {posts[0].published_date ? new Date(posts[0].published_date).toLocaleDateString('no-NO', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</span>
                    <span className="flex items-center gap-1"><User size={14} /> {posts[0].author}</span>
                  </div>
                  <h2 className="text-3xl font-bold text-stone-900 mb-3 group-hover:text-emerald-800 transition-colors" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {posts[0].title}
                  </h2>
                  <p className="text-stone-600 leading-relaxed mb-4 text-lg">{stripHtml(posts[0].excerpt)}</p>
                  <span className="text-emerald-800 font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    Les mer <ChevronRight size={16} />
                  </span>
                </div>
              </article>
            )}

            {/* Rest of posts in grid */}
            {posts.length > 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {posts.slice(1).map((post) => (
                  <article
                    key={post.id}
                    onClick={() => navigate(`/blogg/${post.slug}`)}
                    className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer group"
                    data-testid={`blog-post-${post.slug}`}
                  >
                    {post.cover_image && (
                      <div className="w-full h-48 overflow-hidden">
                        <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-xs text-stone-400 mb-3">
                        {post.category && (
                          <span className="bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded-full font-semibold">
                            {post.category}
                          </span>
                        )}
                        <span>{post.published_date ? new Date(post.published_date).toLocaleDateString('no-NO') : ''}</span>
                      </div>
                      <h2 className="text-xl font-bold text-stone-900 mb-2 group-hover:text-emerald-800 transition-colors" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {post.title}
                      </h2>
                      <p className="text-stone-600 text-sm leading-relaxed line-clamp-3">{stripHtml(post.excerpt)}</p>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-emerald-900 text-white py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Trenger gravsteinen din oppfrisking?
          </h2>
          <p className="text-emerald-100 mb-6">Kontakt oss for et uforpliktende pristilbud</p>
          <a href="tel:48147777" className="inline-block bg-white text-emerald-900 hover:bg-stone-100 rounded-full px-8 py-3 font-semibold transition-colors">
            Ring 48 14 77 77
          </a>
        </div>
      </div>
    </div>
  );
}

function formatBlogContent(content) {
  if (!content) return '';
  // If content already has proper HTML structure, return as-is
  if (content.includes('<p>') && content.includes('</p>')) return content;
  
  // Process plain text or poorly formatted content
  let html = content;
  
  // Convert --- to section dividers
  html = html.replace(/\s*-{3,}\s*/g, '\n<hr class="my-8 border-stone-200" />\n');
  
  // Split into paragraphs by double newlines or single newlines
  const parts = html.split(/\n\n+|\n/);
  
  html = parts.map(part => {
    part = part.trim();
    if (!part) return '';
    // Don't wrap elements that are already HTML blocks
    if (part.startsWith('<hr') || part.startsWith('<img') || part.startsWith('<h') || part.startsWith('<ul') || part.startsWith('<ol') || part.startsWith('<div') || part.startsWith('<blockquote')) {
      return part;
    }
    return `<p>${part}</p>`;
  }).join('\n');
  
  return html;
}

function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/blog/${slug}`);
        if (res.ok) setPost(await res.json());
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-800 rounded-full animate-spin"></div>
    </div>
  );

  if (!post) return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-stone-900 mb-4">Innlegg ikke funnet</h1>
        <button onClick={() => navigate('/blogg')} className="text-emerald-800 font-medium">Tilbake til bloggen</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50">
      <nav className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/blogg')} className="flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors" data-testid="blogpost-back-btn">
            <ArrowLeft size={20} />
            <span className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>Blogg</span>
          </button>
        </div>
      </nav>

      {/* Cover image */}
      {post.cover_image && (
        <div className="w-full h-64 md:h-96 overflow-hidden">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}

      <article className="max-w-3xl mx-auto px-4 py-12" data-testid="blog-article">
        <div className="flex items-center gap-4 text-sm text-stone-400 mb-6 flex-wrap">
          {post.category && (
            <span className="flex items-center gap-1 bg-emerald-50 text-emerald-800 px-3 py-1 rounded-full text-xs font-semibold">
              <Tag size={12} /> {post.category}
            </span>
          )}
          <span className="flex items-center gap-1"><Calendar size={14} /> {post.published_date ? new Date(post.published_date).toLocaleDateString('no-NO', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</span>
          <span className="flex items-center gap-1"><User size={14} /> {post.author}</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-8 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="blogpost-title">
          {post.title}
        </h1>
        <div
          className="prose prose-stone prose-lg max-w-none [&_h2]:font-bold [&_h2]:text-2xl [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:text-stone-900 [&_h3]:font-semibold [&_h3]:text-xl [&_h3]:mt-8 [&_h3]:mb-3 [&_p]:mb-5 [&_p]:leading-relaxed [&_p]:text-stone-700 [&_p]:text-base [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-5 [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-5 [&_li]:mb-2 [&_li]:text-stone-700 [&_strong]:text-stone-900 [&_blockquote]:border-l-4 [&_blockquote]:border-emerald-800 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-stone-600 [&_img]:rounded-xl [&_img]:shadow-lg [&_img]:my-8 [&_img]:max-w-full [&_hr]:my-10 [&_hr]:border-stone-200"
          dangerouslySetInnerHTML={{ __html: formatBlogContent(post.content) }}
          data-testid="blogpost-content"
        />
      </article>

      <div className="border-t border-stone-200 py-8">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <button onClick={() => navigate('/blogg')} className="text-emerald-800 font-medium hover:text-emerald-900 flex items-center gap-2 mx-auto">
            <ArrowLeft size={16} /> Tilbake til bloggen
          </button>
        </div>
      </div>
    </div>
  );
}

export { BlogList, BlogPost };
